import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, 
  Check, 
  ArrowRight,
  Loader2,
  Sparkles,
  Shield,
  Zap,
  Users,
  Gift,
  Mail
} from "lucide-react";

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [requestingTest, setRequestingTest] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeader()
      });
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to load subscription:", error);
    }
  };

  const handleCheckout = async (planId) => {
    if (!user) {
      toast.info("Please create an account first");
      navigate("/register");
      return;
    }

    setCheckoutLoading(planId);
    try {
      const response = await axios.post(
        `${API}/checkout/create`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const starterFeatures = [
    "Unlimited Properties",
    "Unlimited Budgets",
    "Full Budget Creation",
    "P&L Reports",
    "5-Year Projections",
    "Unlimited Downloads",
    "7 Days Access"
  ];

  const professionalFeatures = [
    "Unlimited Properties",
    "Unlimited Budgets",
    "Full Budget Creation",
    "All 5 Report Types",
    "Unlimited Downloads",
    "Custom Scenarios (3)",
    "Budget vs Actuals Tracking",
    "Priority Support",
    "12 Months Access"
  ];

  const testFeatures = [
    "Unlimited Properties",
    "Unlimited Budgets",
    "Full Budget Creation",
    "All 5 Report Types",
    "Unlimited Downloads",
    "Custom Scenarios (3)",
    "Budget vs Actuals Tracking",
    "90 Days Access",
    "No Payment Required"
  ];

  const handleRequestTestAccess = async () => {
    if (!user) {
      toast.info("Please create an account first");
      navigate("/register");
      return;
    }

    setRequestingTest(true);
    try {
      // Send support request for test access
      await axios.post(
        `${API}/support/contact`,
        {
          name: user.name || user.email,
          email: user.email,
          subject: "Test Access Request",
          message: `Hi,\n\nI would like to request test access to the HotelBudget platform.\n\nUser: ${user.name || 'N/A'}\nEmail: ${user.email}\n\nThank you!`
        },
        { headers: getAuthHeader() }
      );
      toast.success("Test access request sent! We'll get back to you shortly.");
    } catch (error) {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setRequestingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/welcome" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#4B5E40] to-[#8FA385] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                HotelBudget
              </span>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/">
                    <Button variant="ghost" className="text-slate-600 hover:text-slate-900 font-medium">
                      Dashboard
                    </Button>
                  </Link>
                  <span className="text-sm text-slate-500">{user.email}</span>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600 hover:text-slate-900 font-medium">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white font-semibold px-6 rounded-full">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700 hover:bg-amber-100">
              SIMPLE PRICING
            </Badge>
            <h1 
              className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Choose Your Plan
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Start with a 7-day trial or unlock full power with our Professional plan.
            </p>

            {subscription?.status === "active" && (
              <div className="mt-6">
                <Badge className="bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
                  <Sparkles className="w-4 h-4 mr-2" />
                  You're on the {subscription.plan_name} plan
                </Badge>
              </div>
            )}
          </div>

          <div className={`grid gap-8 max-w-6xl mx-auto ${user ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-4xl'}`}>
            {/* Starter Plan */}
            <Card className="border-2 border-slate-200 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Starter</h3>
                <p className="text-slate-600 mb-6">Perfect to try the platform</p>
                <div className="mb-6">
                  <span className="text-5xl font-extrabold text-slate-900">$30</span>
                  <span className="text-slate-500 ml-2">/ 7 days</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {starterFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleCheckout("basic")}
                  disabled={checkoutLoading === "basic" || subscription?.status === "active"}
                  className="w-full py-6 text-lg rounded-xl border-2 border-slate-900 bg-white text-slate-900 hover:bg-slate-50 font-semibold"
                >
                  {checkoutLoading === "basic" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : subscription?.status === "active" ? (
                    "Current Plan Active"
                  ) : (
                    "Get Started"
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Professional Plan */}
            <Card className="border-2 border-[#4B5E40] rounded-3xl overflow-hidden shadow-xl relative">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-[#4B5E40] to-[#8FA385] text-white px-4 py-1 rounded-bl-xl text-sm font-semibold">
                BEST VALUE
              </div>
              <CardContent className="p-8 bg-gradient-to-b from-[#F5EDCE] to-white">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Professional</h3>
                <p className="text-slate-600 mb-6">For serious hotel operators</p>
                <div className="mb-6">
                  <span className="text-5xl font-extrabold text-slate-900">$120</span>
                  <span className="text-slate-500 ml-2">/ year</span>
                  <p className="text-emerald-600 text-sm mt-1 font-medium">That's only $10/month!</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {professionalFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700">
                      <Check className="w-5 h-5 text-[#4B5E40] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => handleCheckout("premium")}
                  disabled={checkoutLoading === "premium" || subscription?.status === "active"}
                  className="w-full py-6 text-lg rounded-xl bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white font-semibold shadow-lg"
                >
                  {checkoutLoading === "premium" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : subscription?.status === "active" ? (
                    "Current Plan Active"
                  ) : (
                    <>
                      Start Professional
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Test Access Plan - Only visible to logged in users */}
            {user && (
              <Card className="border-2 border-[#C9A227] rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 relative">
                <div className="absolute top-0 right-0 bg-gradient-to-r from-[#C9A227] to-[#D4B030] text-white px-4 py-1 rounded-bl-xl text-sm font-semibold">
                  FREE ACCESS
                </div>
                <CardContent className="p-8 bg-gradient-to-b from-[#FDF8E8] to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-6 h-6 text-[#C9A227]" />
                    <h3 className="text-2xl font-bold text-slate-900">Test Access</h3>
                  </div>
                  <p className="text-slate-600 mb-6">For team members & partners</p>
                  <div className="mb-6">
                    <span className="text-5xl font-extrabold text-[#C9A227]">FREE</span>
                    <span className="text-slate-500 ml-2">/ 90 days</span>
                    <p className="text-[#C9A227] text-sm mt-1 font-medium">Full platform access!</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {testFeatures.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-700">
                        <Check className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {subscription?.plan === "test" ? (
                    <Button 
                      disabled
                      className="w-full py-6 text-lg rounded-xl bg-[#F5EDCE] text-[#8B7355] font-semibold cursor-not-allowed"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Test Access Active
                    </Button>
                  ) : subscription?.status === "active" ? (
                    <Button 
                      disabled
                      className="w-full py-6 text-lg rounded-xl bg-slate-100 text-slate-500 font-semibold cursor-not-allowed"
                    >
                      You Have an Active Plan
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleRequestTestAccess}
                      disabled={requestingTest}
                      className="w-full py-6 text-lg rounded-xl bg-gradient-to-r from-[#C9A227] to-[#D4B030] hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg"
                    >
                      {requestingTest ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Mail className="w-5 h-5 mr-2" />
                          Request Test Access
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-16 text-slate-500">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span>Secure Payments via Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              <span>Instant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>500+ Hotels Trust Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Additional Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Questions?</h2>
          <p className="text-slate-600 mb-6">
            Not sure which plan is right for you? Start with the Starter plan to try the platform,
            then upgrade anytime to Professional for full features.
          </p>
          <Link to="/register">
            <Button variant="outline" className="rounded-full px-8">
              Create Free Account First
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#4B5E40] to-[#8FA385] rounded-lg flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">HotelBudget</span>
          </div>
          <p className="text-slate-400 text-sm">
            © 2024 HotelBudget. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
